import React from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LabelChip from 'components/chip/LabelChip';
import VisibilityChip from 'components/chip/VisibilityChip';
import { ArticleSummary, ArticleVisibility } from 'stores/ArticleStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';

import ClampedText from './ClampedText';
import { formatAuthors, formatReadTime } from './articleUtils';

type Props = {
  article: ArticleSummary;
  onOpen: (articleId: string) => void;
  /** editable 記事の編集導線（省略時はメニュー非表示） */
  onEdit?: (articleId: string) => void;
  /** editable 記事の削除導線 */
  onDelete?: (article: ArticleSummary) => void;
  /** editable 記事の公開設定変更導線 */
  onChangeVisibility?: (article: ArticleSummary, visibility: ArticleVisibility) => void;
};

const VISIBILITY_OPTIONS: ArticleVisibility[] = ['PUBLIC', 'INTERNAL', 'PRIVATE'];

/**
 * 記事カード。DevInsights の一覧・グループ表示で使用する。
 * editable=true かつ操作ハンドラが渡された場合のみ、右上にオーバーフローメニュー（編集/公開設定/削除）を表示する。
 */
const ArticleCard: React.FC<Props> = ({
  article,
  onOpen,
  onEdit,
  onDelete,
  onChangeVisibility,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [visibilityExpanded, setVisibilityExpanded] = React.useState(false);

  const showMenu = article.editable && (onEdit || onDelete || onChangeVisibility);

  const readTime = formatReadTime(article.charCount);
  const authorText = formatAuthors(article.authors);

  const closeMenu = () => {
    setMenuAnchor(null);
    setVisibilityExpanded(false);
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        position: 'relative',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        // ホバー時に上端へアクセントラインを差し込み、わずかに浮かせる
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          bgcolor: 'primary.main',
          transform: 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 0.25s ease',
        },
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
          boxShadow: (theme) => `0 6px 20px ${theme.palette.action.hover}`,
        },
        '&:hover::before': { transform: 'scaleX(1)' },
      }}
    >
      {showMenu && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      <CardActionArea
        onClick={() => onOpen(article.articleId)}
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
        }}
      >
        {/* ラベル + 公開設定 */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5, pr: 3 }}>
          {article.labels.map((l) => (
            <LabelChip key={l.labelId} name={l.name} color={l.color} />
          ))}
          <VisibilityChip visibility={article.visibility} />
        </Stack>

        {/* タイトル（2行省略。あふれたらホバーで全文） */}
        <ClampedText
          text={article.title}
          lines={2}
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: article.subtitle ? 0.25 : 0.75, lineHeight: 1.35 }}
        />

        {/* サブタイトル */}
        {article.subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.75, display: 'block' }}
            noWrap
          >
            {article.subtitle}
          </Typography>
        )}

        {/* 説明（小さめ・2行省略。あふれたらホバーで全文） */}
        {article.description && (
          <ClampedText
            text={article.description}
            lines={2}
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1.5, flex: 1, fontSize: '0.78rem', lineHeight: 1.6 }}
          />
        )}

        {/* メタ情報 */}
        <Box sx={{ width: '100%', mt: 'auto' }}>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
            {authorText && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {authorText}
                </Typography>
              </Stack>
            )}
            {readTime && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {readTime}
                </Typography>
              </Stack>
            )}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ ml: 'auto !important', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {formatDate(article.createdAt)}
              <ArrowForwardIcon sx={{ fontSize: 13 }} />
            </Typography>
          </Stack>
        </Box>
      </CardActionArea>

      {/* オーバーフローメニュー */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: { borderRadius: 2, border: 1, borderColor: 'divider', minWidth: 180, mt: 0.5 },
          },
        }}
      >
        {onEdit && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onEdit(article.articleId);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>編集</ListItemText>
          </MenuItem>
        )}
        {onChangeVisibility && (
          <MenuItem onClick={() => setVisibilityExpanded((v) => !v)}>
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
              公開設定を変更
            </ListItemText>
          </MenuItem>
        )}
        {onChangeVisibility &&
          visibilityExpanded &&
          VISIBILITY_OPTIONS.map((v) => (
            <MenuItem
              key={v}
              selected={v === article.visibility}
              onClick={() => {
                closeMenu();
                if (v !== article.visibility) {
                  onChangeVisibility(article, v);
                }
              }}
              sx={{ pl: 4, py: 0.75 }}
            >
              <VisibilityChip visibility={v} />
            </MenuItem>
          ))}
        {onDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                closeMenu();
                onDelete(article);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>削除</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Card>
  );
};

export default ArticleCard;
